import { environment } from "src/environments/environment";
import { ICardboardService } from "./cardboard.interface";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { catchError, map } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import Swal from "sweetalert2";
import { IHelperService } from "../helper/helper";
import { TranslateService } from "@ngx-translate/core";
import * as Sentry from "@sentry/angular-ivy";

@Injectable({
  providedIn: "root",
})
export class CardboardService implements ICardboardService {
  constructor(
    private helperService: IHelperService,
    private translate: TranslateService,
    private http: HttpClient,
  ) {}

  public getCardboardCompanyByVAT(VAT: string): Observable<any> {
    return this.http
      .get<any>(
        `${environment.cardboardURL}/api/cardboard/getCardboardCompanyByVAT?VAT=${VAT}`,
      )
      .pipe(
        map((response: any) => {
          if (response.status === 200) {
            return response.data;
          } else {
            var id = Sentry.captureMessage(
              `Server request failed with ${response.status}`,
            );
            Swal.fire({
              icon: "error",
              title: `${this.translate.instant(
                "General_Error",
              )} \n Trace ID: ${id}`,
              text: response.errors.join(", "),
            });
          }
        }),
      )
      .pipe(catchError(this.helperService.errorHandler));
  }

  public getCardboardCompanyBankDetailsByVAT(vat: string): Observable<any> {
    return this.http
      .get<any>(
        `${environment.cardboardURL}/api/cardboard/getCardboardCompanyBankDetailsByVAT?VAT=${vat}`,
      )
      .pipe(
        map((response: any) => {
          if (response.status === 200) {
            return response.data;
          } else {
            let id = Sentry.captureMessage(
              `Server request failed with ${response.status}`,
            );
            Swal.fire({
              icon: "error",
              title: `${this.translate.instant(
                "General_Error",
              )} \n Trace ID: ${id}`,
              text: response.errors.join(", "),
            });
          }
        }),
      )
      .pipe(catchError(this.helperService.errorHandler));
  }

  public getCardboardCompanyById(id: number): Observable<any> {
    return this.http
      .get<any>(
        `${environment.cardboardURL}/api/cardboard/getCardboardCompanyById?id=${id}`,
      )
      .pipe(
        map((response: any) => {
          if (response.status === 200) {
            return response.data;
          } else {
            var id = Sentry.captureMessage(
              `Server request failed with ${response.status}`,
            );
            Swal.fire({
              icon: "error",
              title: `${this.translate.instant(
                "General_Error",
              )}\n Trace ID: ${id}`,
              text: response.errors.join(", "),
            });
          }
        }),
      )
      .pipe(catchError(this.helperService.errorHandler));
  }

  public companyRegistration(cardboardCompany: any): Observable<any> {
    return this.http
      .post<any>(
        `${environment.cardboardURL}/api/cardboard/companyRegistration`,
        cardboardCompany,
      )
      .pipe(
        map((response: any) => {
          if (response.status === 200) {
            return response.data;
          } else {
            var id = Sentry.captureMessage(
              `Server request failed with ${response.status}`,
            );
            Swal.fire({
              icon: "error",
              title: `${this.translate.instant(
                "General_Error",
              )}\n Trace ID: ${id}`,
              text: response.errors.join(", "),
            });
          }
        }),
      )
      .pipe(catchError(this.helperService.errorHandler));
  }

  public updateCardboardCompany(cardboardCompany: any): Observable<any> {
    return this.http
      .put<any>(
        `${environment.cardboardURL}/api/cardboard/updateCardboardCompany`,
        cardboardCompany,
      )
      .pipe(
        map((response: any) => {
          if (response.status === 200) {
            return response.data;
          } else {
            var id = Sentry.captureMessage(
              `Server request failed with ${response.status}`,
            );
            Swal.fire({
              icon: "error",
              title: `${this.translate.instant(
                "General_Error",
              )}\n Trace ID: ${id}`,
              text: response.errors.join(", "),
            });
          }
        }),
      )
      .pipe(catchError(this.helperService.errorHandler));
  }

  public setRepresentatives(representatives: any): Observable<any> {
    return this.http
      .put<any>(
        `${environment.cardboardURL}/api/cardboard/setRepresentativesToCompany`,
        representatives,
      )
      .pipe(
        map((response: any) => {
          if (response.status === 200) {
            return response.data;
          } else {
            var id = Sentry.captureMessage(
              `Server request failed with ${response.status}`,
            );
            Swal.fire({
              icon: "error",
              title: `${this.translate.instant(
                "General_Error",
              )}\n Trace ID: ${id}`,
              text: response.errors.join(", "),
            });
          }
        }),
      )
      .pipe(catchError(this.helperService.errorHandler));
  }

  public uploadDocumentToCompany(
    companyId: number,
    documentType: string,
    file: any,
  ): Observable<any> {
    const formData: FormData = new FormData();
    formData.append("fileDocumentType", documentType);
    formData.append("file", file, file.name);
    formData.append("id", companyId.toString());

    return this.http
      .post<any>(
        `${environment.cardboardURL}/api/cardboard/uploadDocumentToCompany`,
        formData,
      )
      .pipe(
        map((response: any) => {
          if (response.status === 200) {
            return response.data;
          } else {
            var id = Sentry.captureMessage(
              `Server request failed with ${response.status}`,
            );
            Swal.fire({
              icon: "error",
              title: `${this.translate.instant(
                "General_Error",
              )}\n Trace ID: ${id}`,
              text: response.errors.join(", "),
            });
          }
        }),
      );
  }

  public createCardboardNDA(data: any): Observable<any> {
    return this.http
      .post<any>(
        `${environment.cardboardURL}/api/cardboard/createCardboardNDA`,
        data,
      )
      .pipe(
        map((response: any) => {
          if (response.status === 200) {
            return response.data;
          } else {
            var id = Sentry.captureMessage(
              `Server request failed with ${response.status}`,
            );
            Swal.fire({
              icon: "error",
              title: `${this.translate.instant(
                "General_Error",
              )} \n Trace ID: ${id}`,
              text: response.errors.join(", "),
            });
          }
        }),
      )
      .pipe(catchError(this.helperService.errorHandler));
  }

  public getCardboardUnsubscribeByEmail(email: string): Observable<any> {
    return this.http
      .get<any>(
        `${environment.cardboardURL}/api/cardboard/getCardboardUnsubscribeByEmail?email=${email}`,
      )
      .pipe(
        map((response: any) => {
          if (response.status === 200) {
            return response.data;
          } else {
            var id = Sentry.captureMessage(
              `Server request failed with ${response.status}`,
            );
            Swal.fire({
              icon: "error",
              title: `${this.translate.instant(
                "General_Error",
              )}\n Trace ID: ${id}`,
              text: response.errors.join(", "),
            });
          }
        }),
      )
      .pipe(catchError(this.helperService.errorHandler));
  }

  public unsubscribeToEmails(email: string): Observable<any> {
    return this.http
      .post<any>(
        `${environment.cardboardURL}/api/cardboard/UnsubscribeToEmails`,
        {
          email,
        },
      )
      .pipe(
        map((response: any) => {
          if (response.status === 200) {
            return response.data;
          } else {
            var id = Sentry.captureMessage(
              `Server request failed with ${response.status}`,
            );
            Swal.fire({
              icon: "error",
              title: `${this.translate.instant(
                "General_Error",
              )}\n Trace ID: ${id}`,
              text: response.errors.join(", "),
            });
          }
        }),
      )
      .pipe(catchError(this.helperService.errorHandler));
  }

  public deleteUser(user: any): Observable<any> {
    return this.http
      .post<any>(`${environment.cardboardURL}/api/cardboard/deleteUser`, user)
      .pipe(
        map((response: any) => {
          if (response.status === 200) {
            return response.data;
          } else {
            var id = Sentry.captureMessage(
              `Server request failed with ${response.status}`,
            );
            Swal.fire({
              icon: "error",
              title: `${this.translate.instant(
                "General_Error",
              )}\n Trace ID: ${id}`,
              text: response.errors.join(", "),
            });
          }
        }),
      )
      .pipe(catchError(this.helperService.errorHandler));
  }

  saveSystemLogs(logs: any): Observable<any> {
    return this.http
      .post<any>(
        `${environment.cardboardURL}/api/systemLogs/saveSystemLogs`,
        logs,
      )
      .pipe(
        map((response: any) => {
          if (response.status === 200) {
            return response.data;
          }
        }),
      );
  }

  getCardboardCompanySignature(
    id: number,
    email: string,
    contractType: string,
  ): Observable<any> {
    return this.http
      .get<any>(
        `${environment.cardboardURL}/api/cardboard/getCardboardCompanySignature?id=${id}&email=${email}&contractType=${contractType}`,
      )
      .pipe(
        map((response: any) => {
          if (response.status === 200) {
            return response.data;
          } else {
            const id = Sentry.captureMessage(
              `Server request failed with ${response.status}`,
            );

            Swal.fire({
              icon: "error",
              title: `${this.translate.instant(
                "General_Error",
              )}\n Trace ID: ${id}`,
              text: response.errors.join(", "),
            });
          }
        }),
      )
      .pipe(catchError(this.helperService.errorHandler));
  }

  uploadSignedContract(form: any, file: any): Observable<any> {
    const formData: FormData = new FormData();
    formData.append("id", form.id);
    formData.append("uniqueCode", form.uniqueCode);
    formData.append("contractType", form.contractType);
    formData.append("file", file, file.name);

    return this.http
      .post<any>(
        `${environment.cardboardURL}/api/cardboard/uploadSignedContract`,
        formData,
      )
      .pipe(
        map((response: any) => {
          if (response.status === 200) {
            return response.data;
          } else {
            var id = Sentry.captureMessage(
              `Server request failed with ${response.status}`,
            );
            Swal.fire({
              icon: "error",
              title: `${this.translate.instant(
                "General_Error",
              )}\n Trace ID: ${id}`,
              text: response.errors.join(", "),
            });
          }
        }),
      );
  }

  getLegalFormsByProjectId(projectId: string): Observable<any> {
    return this.http
      .get<any>(`${environment.crmURL}/api/projects/getLegalForms/${projectId}`)
      .pipe(
        map((response: any) => {
          if (response.status === 200) {
            return response.data;
          } else {
            var id = Sentry.captureMessage(
              `Server request failed with ${response.status}`,
            );
            Swal.fire({
              icon: "error",
              title: `${this.translate.instant(
                "General_Error",
              )}\n Trace ID: ${id}`,
              text: response.errors.join(", "),
            });
          }
        }),
      )
      .pipe(catchError(this.helperService.errorHandler));
  }

  getLegalFormsByCountry(country: string): Observable<any> {
    return this.http
      .get<any>(`${environment.crmURL}/api/countries/${country}/legal-forms`)
      .pipe(
        map((response: any) => {
          if (response.status === 200) {
            return response.data;
          } else {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: response.errors.join(", "),
            });
          }
        }),
      )
      .pipe(catchError(this.helperService.errorHandler));
  }

  getPhoneCountries(projectId: string): Observable<any[]> {
    return this.http
      .get<any>(`${environment.crmURL}/api/countries/${projectId}/phoneCodes`)
      .pipe(
        map((response: any) => {
          if (response.status === 200) {
            return response.data;
          } else {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: response.errors.join(", "),
            });
          }
        }),
      )
      .pipe(catchError(this.helperService.errorHandler));
  }

  getStatesByCountry(country: string): Observable<any> {
    return this.http
      .get<any>(
        `${environment.crmURL}/api/countries/getStatesByCountry?countryName=${country}`,
      )
      .pipe(
        map((response: any) => {
          if (response.status === 200) {
            return response.data;
          } else {
            var id = Sentry.captureMessage(
              `Server request failed with ${response.status}`,
            );
            Swal.fire({
              icon: "error",
              title: `${this.translate.instant(
                "General_Error",
              )}\n Trace ID: ${id}`,
              text: response.errors.join(", "),
            });
          }
        }),
      )
      .pipe(catchError(this.helperService.errorHandler));
  }

  checkDuplicatedVAT(vat: string): Observable<any> {
    return this.http
      .get<any>(
        `${environment.cardboardURL}/api/cardboard/checkDuplicatedVAT?vat=${vat}`,
      )
      .pipe(
        map((response: any) => {
          if (response.status === 200) {
            return response.data;
          } else {
            var id = Sentry.captureMessage(
              `Server request failed with ${response.status}`,
            );
            Swal.fire({
              icon: "error",
              title: `${this.translate.instant(
                "General_Error",
              )}\n Trace ID: ${id}`,
              text: response.errors.join(", "),
            });
          }
        }),
      )
      .pipe(catchError(this.helperService.errorHandler));
  }

  getCompanyInfo(vat: string): Observable<any> {
    return this.http
      .get<any>(
        `${environment.cardboardURL}/api/cardboard/getCompanyInfo?vat=${vat}`,
      )
      .pipe(
        map((response: any) => {
          if (response.status === 200) {
            return response.data;
          } else {
            var id = Sentry.captureMessage(
              `Server request failed with ${response.status}`,
            );
            Swal.fire({
              icon: "error",
              title: `${this.translate.instant(
                "General_Error",
              )}\n Trace ID: ${id}`,
              text: response.errors.join(", "),
            });
          }
        }),
      )
      .pipe(catchError(this.helperService.errorHandler));
  }

  validateDNI(dni: string): Observable<any> {
    return this.http
      .get<any>(`https://documentconverter.smartec.pro/validate/dni?dni=${dni}`)
      .pipe(
        map((response: any) => {
          if (response.status === 200) {
            return response.data;
          } else {
            var id = Sentry.captureMessage(
              `Server request failed with ${response.status}`,
            );
            Swal.fire({
              icon: "error",
              title: `${this.translate.instant(
                "General_Error",
              )}\n Trace ID: ${id}`,
              text: response.errors.join(", "),
            });
          }
        }),
      )
      .pipe(catchError(this.helperService.errorHandler));
  }

  saveBankInfoAccount(bankDetails: any): Observable<any> {
    return this.http
      .post<any>(
        `${environment.cardboardURL}/api/cardboard/saveBankInfoAccount`,
        bankDetails,
      )
      .pipe(
        map((response: any) => {
          if (response.status === 200) {
            return response.data;
          } else {
            let id = Sentry.captureMessage(
              `Server request failed with ${response.status}`,
            );

            Swal.fire({
              icon: "error",
              title: `${this.translate.instant(
                "General_Error",
              )}\n Trace ID: ${id}`,
              text: response.errors.join(", "),
            });
          }
        }),
      )
      .pipe(catchError(this.helperService.errorHandler));
  }

  public getBankDetailsFromIban(iban: string): Observable<any> {
    return this.http
      .post<any>(`https://documentconverter.smartec.pro/iban/decode`, { iban })
      .pipe(
        map((response: any) => {
          if (response.status === 200) {
            return response.data;
          } else {
            Swal.fire({
              icon: "error",
              title: "Error",
              html: `${response.errors.join(", ")}`,
            });
          }
        }),
      )
      .pipe(catchError(this.helperService.errorHandler));
  }

  public getBanksByCountry(country: string): Observable<any> {
    return this.http
      .get<any>(
        `${environment.crmURL}/api/companies/getAllBanksByCountry?country=${country}`,
      )
      .pipe(
        map((response: any) => {
          if (response.status === 200) {
            return response.data;
          } else {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: response.errors[0]?.replace(
                "One or more errors occurred.",
                "",
              ),
            });
          }
        }),
      )
      .pipe(catchError(this.helperService.errorHandler));
  }

  getRepresentativeTitlesByProject(projectId: string): Observable<any> {
    return this.http
      .get<any>(
        `${environment.crmURL}/api/projects/${projectId}/representativeTitles`,
      )
      .pipe(
        map((response: any) => {
          if (response.status === 200) {
            return response.data;
          } else {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: response.errors.join(", "),
            });
          }
        }),
      )
      .pipe(catchError(this.helperService.errorHandler));
  }

  getInvoicesByCompany(companyId: string): Observable<any> {
    return this.http
      .get<any>(
        `${environment.crmURL}/api/invoicePayments/search/${companyId}/project/${environment.projectId}`,
      )
      .pipe(
        map((response: any) => {
          if (response.status === 200) {
            return response.data;
          } else {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: response.errors.join(", "),
            });
          }
        }),
      )
      .pipe(catchError(this.helperService.errorHandler));
  }

  isApprovedInvoice(companyId: string): Observable<any> {
    return this.http
      .get<any>(
        `${environment.crmURL}/api/invoicePayments/is-approved/${companyId}/project/${environment.projectId}`,
      )
      .pipe(
        map((response: any) => {
          if (response.status === 200) {
            return response.data;
          } else {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: response.errors.join(", "),
            });
          }
        }),
      )
      .pipe(catchError(this.helperService.errorHandler));
  }

  deleteInvoice(invoice: any): Observable<any> {
    return this.http
      .post<any>(`${environment.crmURL}/api/invoicePayments/delete-invoice`, {
        invoiceId: invoice.id,
        companyId: invoice.cardboardCompanyId.toString(),
        reason: invoice.reason,
      })
      .pipe(
        map((response: any) => {
          if (response.status === 200) {
            return response.data;
          } else {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: response.errors.join(", "),
            });
          }
        }),
      )
      .pipe(catchError(this.helperService.errorHandler));
  }

  getServices(): any[] {
    return [
      {
        id: "quick-facts",
        img: "/assets/images/services/service_1.webp",
        sm: "/assets/images/services/sm/service_1.webp",
        icon: "fa-solid fa-circle-info",
        title: "GB_Registration_Service2_Title",
        description: "GB_Registration_Service2_Description",
        iconText: "GB_Registration_ReadMore",
      },
      {
        id: "effectiveness-of-our-approach",
        img: "/assets/images/services/service_2.webp",
        sm: "/assets/images/services/sm/service_2.webp",
        icon: "fa-solid fa-briefcase",
        title: "GB_Registration_Service3_Title",
        description: "GB_Registration_Service3_Description",
        iconText: "GB_Registration_ReadMore",
      },
      {
        id: "how-does-our-model-work",
        img: "/assets/images/services/service_3.webp",
        sm: "/assets/images/services/sm/service_3.webp",
        icon: "fa-solid fa-clipboard-list",
        title: "GB_Registration_Service4_Title",
        description: "GB_Registration_Service4_Description",
        iconText: "GB_Registration_ReadMore",
      },
      {
        id: "unique-expertise-in-europe",
        img: "/assets/images/services/service_4.webp",
        sm: "/assets/images/services/sm/service_4.webp",
        icon: "fa-solid fa-scale-balanced",
        title: "GB_Registration_Service5_Title",
        description: "GB_Registration_Service5_Description",
        iconText: "GB_Registration_ReadMore",
      },
      {
        id: "key-advantages-for-damaged-companies",
        img: "/assets/images/services/service_5.webp",
        sm: "/assets/images/services/sm/service_5.webp",
        icon: "fa-solid fa-newspaper",
        title: "GB_Registration_Service6_Title",
        description: "GB_Registration_Service6_Description",
        iconText: "GB_Registration_ReadMore",
      },
      {
        id: "next-steps",
        img: "/assets/images/services/service_6.webp",
        sm: "/assets/images/services/sm/service_6.webp",
        icon: "fa-solid fa-shoe-prints",
        title: "GB_Registration_Service1_Title",
        description: "GB_Registration_Service1_Description",
        iconText: "GB_Registration_ReadMore",
      },
    ];
  }

  getService(id: string): any {
    switch (id) {
      case "quick-facts":
        return {
          img: "/assets/images/services/service_1.webp",
          title: "GB_Registration_Service2_Title",
          subTitle: "GB_Registration_Service2_SubTitle_Page",
          description: "GB_Registration_Service2_Description_Page",
          blueSection:
            "GB_Registration_Service2_BusinessPlanMarket_BlueSection_Page",
          whatWeDoTitle: "GB_Registration_Service2_WhatWeDo_Title_Page",
          whatWeDoDescription:
            "GB_Registration_Service2_WhatWeDo_Description_Page",
          businessPlanMarketTitle:
            "GB_Registration_Service2_BusinessPlanMarket_Title_Page",
          businessPlanMarketDescription:
            "GB_Registration_Service2_BusinessPlanMarket_Description_Page",
        };

      case "effectiveness-of-our-approach":
        return {
          img: "/assets/images/services/service_2.webp",
          title: "GB_Registration_Service3_Title",
          subTitle: "GB_Registration_Service3_SubTitle_Page",
          description: "GB_Registration_Service3_Description_Page",
          blueSection:
            "GB_Registration_Service3_BusinessPlanMarket_BlueSection_Page",
          whatWeDoTitle: "GB_Registration_Service3_WhatWeDo_Title_Page",
          whatWeDoDescription:
            "GB_Registration_Service3_WhatWeDo_Description_Page",
          businessPlanMarketTitle:
            "GB_Registration_Service3_BusinessPlanMarket_Title_Page",
          businessPlanMarketDescription:
            "GB_Registration_Service3_BusinessPlanMarket_Description_Page",
        };

      case "how-does-our-model-work":
        return {
          img: "/assets/images/services/service_3.webp",
          title: "GB_Registration_Service4_Title",
          subTitle: "GB_Registration_Service4_SubTitle_Page",
          description: "GB_Registration_Service4_Description_Page",
          blueSection:
            "GB_Registration_Service4_BusinessPlanMarket_BlueSection_Page",
          whatWeDoTitle: "GB_Registration_Service4_WhatWeDo_Title_Page",
          whatWeDoDescription:
            "GB_Registration_Service4_WhatWeDo_Description_Page",
          businessPlanMarketTitle:
            "GB_Registration_Service4_BusinessPlanMarket_Title_Page",
          businessPlanMarketDescription:
            "GB_Registration_Service4_BusinessPlanMarket_Description_Page",
        };

      case "unique-expertise-in-europe":
        return {
          img: "/assets/images/services/service_4.webp",
          title: "GB_Registration_Service5_Title",
          subTitle: "GB_Registration_Service5_SubTitle_Page",
          description: "GB_Registration_Service5_Description_Page",
          blueSection:
            "GB_Registration_Service5_BusinessPlanMarket_BlueSection_Page",
          whatWeDoTitle: "GB_Registration_Service5_WhatWeDo_Title_Page",
          whatWeDoDescription:
            "GB_Registration_Service5_WhatWeDo_Description_Page",
          businessPlanMarketTitle:
            "GB_Registration_Service5_BusinessPlanMarket_Title_Page",
          businessPlanMarketDescription:
            "GB_Registration_Service5_BusinessPlanMarket_Description_Page",
        };

      case "key-advantages-for-damaged-companies":
        return {
          img: "/assets/images/services/service_5.webp",
          title: "GB_Registration_Service6_Title",
          subTitle: "GB_Registration_Service6_SubTitle_Page",
          description: "GB_Registration_Service6_Description_Page",
          blueSection:
            "GB_Registration_Service6_BusinessPlanMarket_BlueSection_Page",
          whatWeDoTitle: "GB_Registration_Service6_WhatWeDo_Title_Page",
          whatWeDoDescription:
            "GB_Registration_Service6_WhatWeDo_Description_Page",
          businessPlanMarketTitle:
            "GB_Registration_Service6_BusinessPlanMarket_Title_Page",
          businessPlanMarketDescription:
            "GB_Registration_Service6_BusinessPlanMarket_Description_Page",
        };

      case "next-steps":
        return {
          img: "/assets/images/services/service_6.webp",
          title: "GB_Registration_Service1_Title",
          subTitle: "GB_Registration_Service1_SubTitle_Page",
          description: "GB_Registration_Service1_Description_Page",
          blueSection:
            "GB_Registration_Service1_BusinessPlanMarket_BlueSection_Page",
          whatWeDoTitle: "GB_Registration_Service1_WhatWeDo_Title_Page",
          whatWeDoDescription:
            "GB_Registration_Service1_WhatWeDo_Description_Page",
          businessPlanMarketTitle:
            "GB_Registration_Service1_BusinessPlanMarket_Title_Page",
          businessPlanMarketDescription:
            "GB_Registration_Service1_BusinessPlanMarket_Description_Page",
        };
    }
  }

  getProject(id: string): any {
    switch (id) {
      case "key-facts-of-the-cartels":
        return {
          id,
          img: "/assets/images/projects/p1.webp",
          title: "GB_Registration_Project1_Title_Page",
          subtitle: "GB_Registration_Project1_SubTitle_Page",
          description: "GB_Registration_Project1_Description_Page",
          blueSection: "GB_Registration_Project1_Description_BlueSection_Page",
          whatWeDoTitle: "GB_Registration_Project1_WhatWeDo_Title_Page",
          whatWeDoDescription:
            "GB_Registration_Project1_WhatWeDo_Description_Page",
          projectDetailsClient:
            "GB_Registration_Project1_ProjectDetails_Client_Page",
          projectDetailsLocation:
            "GB_Registration_Project1_ProjectDetails_Location_Page",
          projectDetailsCompleted:
            "GB_Registration_Project1_ProjectDetails_Completed_Page",
          navigation: {
            next: "appels-procedures",
          },
        };

      case "appels-procedures":
        return {
          id,
          img: "/assets/images/projects/p2.webp",
          title: "GB_Registration_Project2_Title_Page",
          subtitle: "GB_Registration_Project2_SubTitle_Page",
          description: "GB_Registration_Project2_Description_Page",
          blueSection: "GB_Registration_Project2_Description_BlueSection_Page",
          whatWeDoTitle: "GB_Registration_Project2_WhatWeDo_Title_Page",
          whatWeDoDescription:
            "GB_Registration_Project2_WhatWeDo_Description_Page",
          projectDetailsClient:
            "GB_Registration_Project2_ProjectDetails_Client_Page",
          projectDetailsLocation:
            "GB_Registration_Project2_ProjectDetails_Location_Page",
          projectDetailsCompleted:
            "GB_Registration_Project2_ProjectDetails_Completed_Page",
          navigation: {
            back: "key-facts-of-the-cartels",
            next: "affected-markets",
          },
        };

      case "affected-markets":
        return {
          id,
          img: "/assets/images/projects/p3.webp",
          title: "GB_Registration_Project3_Title_Page",
          subtitle: "GB_Registration_Project3_SubTitle_Page",
          description: "GB_Registration_Project3_Description_Page",
          blueSection: "GB_Registration_Project3_Description_BlueSection_Page",
          whatWeDoTitle: "GB_Registration_Project3_WhatWeDo_Title_Page",
          whatWeDoDescription:
            "GB_Registration_Project3_WhatWeDo_Description_Page",
          projectDetailsClient:
            "GB_Registration_Project3_ProjectDetails_Client_Page",
          projectDetailsLocation:
            "GB_Registration_Project3_ProjectDetails_Location_Page",
          projectDetailsCompleted:
            "GB_Registration_Project3_ProjectDetails_Completed_Page",
          navigation: {
            back: "appels-procedures",
            next: "cartel-members",
          },
        };

      case "cartel-members":
        return {
          id,
          img: "/assets/images/projects/p4.webp",
          title: "GB_Registration_Project4_Title_Page",
          subtitle: "GB_Registration_Project4_SubTitle_Page",
          description: "GB_Registration_Project4_Description_Page",
          blueSection: "GB_Registration_Project4_Description_BlueSection_Page",
          whatWeDoTitle: "GB_Registration_Project4_WhatWeDo_Title_Page",
          whatWeDoDescription:
            "GB_Registration_Project4_WhatWeDo_Description_Page",
          projectDetailsClient:
            "GB_Registration_Project4_ProjectDetails_Client_Page",
          projectDetailsLocation:
            "GB_Registration_Project4_ProjectDetails_Location_Page",
          projectDetailsCompleted:
            "GB_Registration_Project4_ProjectDetails_Completed_Page",
          navigation: {
            back: "affected-markets",
            next: "limitation-periods",
          },
        };

      case "limitation-periods":
        return {
          id,
          img: "/assets/images/projects/p5.webp",
          title: "GB_Registration_Project5_Title_Page",
          subtitle: "GB_Registration_Project5_SubTitle_Page",
          description: "GB_Registration_Project5_Description_Page",
          blueSection: "GB_Registration_Project5_Description_BlueSection_Page",
          whatWeDoTitle: "GB_Registration_Project5_WhatWeDo_Title_Page",
          whatWeDoDescription:
            "GB_Registration_Project5_WhatWeDo_Description_Page",
          projectDetailsClient:
            "GB_Registration_Project5_ProjectDetails_Client_Page",
          projectDetailsLocation:
            "GB_Registration_Project5_ProjectDetails_Location_Page",
          projectDetailsCompleted:
            "GB_Registration_Project5_ProjectDetails_Completed_Page",
          navigation: {
            back: "cartel-members",
            next: "how-to-prepare-your-information",
          },
        };

      case "how-to-prepare-your-information":
        return {
          id,
          img: "/assets/images/projects/p6.webp",
          title: "GB_Registration_Project6_Title_Page",
          subtitle: "GB_Registration_Project6_SubTitle_Page",
          description: "GB_Registration_Project6_Description_Page",
          blueSection: "GB_Registration_Project6_Description_BlueSection_Page",
          whatWeDoTitle: "GB_Registration_Project6_WhatWeDo_Title_Page",
          whatWeDoDescription:
            "GB_Registration_Project6_WhatWeDo_Description_Page",
          projectDetailsClient:
            "GB_Registration_Project6_ProjectDetails_Client_Page",
          projectDetailsLocation:
            "GB_Registration_Project6_ProjectDetails_Location_Page",
          projectDetailsCompleted:
            "GB_Registration_Project6_ProjectDetails_Completed_Page",
          navigation: {
            back: "limitation-periods",
          },
        };
    }
  }

  downloadBrochure(): Observable<Blob> {
    let language = localStorage.getItem("gb_landing_page");
    return this.http.get<Blob>(
      `${environment.cardboardURL}/api/cardboard/download-brochure/${language}`,
      { responseType: "blob" as "json" },
    );
  }

  getCompanyStatus(vat: string): Observable<any> {
    return this.http
      .get<any>(
        `${environment.cardboardURL}/api/cardboard/company/status/${vat}`,
      )
      .pipe(
        map((response: any) => {
          if (response.status === 200) {
            return response.data;
          } else {
            let id = Sentry.captureMessage(
              "General Error on GET:getCompanyStatus endpoint",
            );
            Swal.fire({
              icon: "error",
              title: `${this.translate.instant("General_Error")}`,
              text: `${response.errors.join(", ")} \n Report ID: \n ${id}`,
            });
          }
        }),
      );
  }

  getSignatureStatus(signerId: string): Observable<any> {
    return this.http
      .get<any>(
        `${environment.cardboardURL}/api/cardboard/signature/status/${signerId}`,
      )
      .pipe(
        map((response: any) => {
          if (response.status === 200) {
            return response.data;
          } else {
            let id = Sentry.captureMessage(
              "General Error on GET:getSignatureStatus endpoint",
            );
            Swal.fire({
              icon: "error",
              title: `${this.translate.instant("General_Error")}`,
              text: `${response.errors.join(", ")} \n Report ID: \n ${id}`,
            });
          }
        }),
      );
  }

  setLoggingView(companyId: string, path: string): void {
    if (!companyId || !path) return;
    this.http
      .post<any>(`${environment.cardboardURL}/api/cardboard/loggingViews`, {
        companyId,
        path,
      })
      .subscribe({
        error: (error) => console.error(error),
      });
  }
}
